import * as R from "ramda";
import * as React from "react";
import { connect } from "react-redux";
import { Dispatch } from "redux";
import { IAction } from "reduxsauce";
import { CourseInstructionPartOperations } from "../../../../../course-author/instruction/parts/CourseInstructionPart.types";
import { Creators as AllCreators } from "../../../../../../actions";
import { IFileUploadResponseData } from "../../../../../../api/CourseAuthorApi";
import { AxiosInterceptedResult } from "../../../../../../api/interceptors";
import { IState } from "../../../../../../App/reducer";
import { courseInstructionPartViews } from "../../../../../../utils/courseViews";
import { PreferredLocale } from "../../../../../../utils/locales";
import { HTMLEditor } from "../../../../../course-author/html-editor/Editor.container";
import { HTMLRenderer } from "../../../../../course-author/html-editor/Renderer";
import { IHTMLEditorProps } from "../../../../../course-author/html-editor/types";
import { ICourseResolveDisplayResponse, ICourseVoiceOver, ITeachingContent } from "../../../../../course-author/types";
import ToggleDisplay from "../../../../../utils/ToggleDisplay";
import { EditVoiceOverAudio, MarginSpace } from "./EditVoiceOverAudio";
import { mkImageUrl } from "../../../../../../actions/course-author/helpers";
import { BackgroundPreviewBox } from "../BackgroundOptions";

const Creators = (AllCreators as any).App.CourseAuthor;

export interface ITeachingPartOwnProps {
  currentLocale: PreferredLocale;
  edit: boolean;
  teachingPart: ITeachingContent;
  teachingOperations: CourseInstructionPartOperations;
  toggleEdit: () => void;
  containerRef: React.RefObject<HTMLDivElement>;
}

export interface ITeachingDispatchProps {
  uploadVoiceOverAudio: (files: FileList | null, link: string | null) => void;
  resolve: (key: string, data: string[]) => void;
}

export interface ITeachingPartStateProps {
  mediaFiles: Array<[string, any]>;
  uploadFile: (file: File) => Promise<AxiosInterceptedResult<IFileUploadResponseData[]>>;
  uploadFileViaUrl: (link: string) => Promise<AxiosInterceptedResult<IFileUploadResponseData>>;
}

export interface ITeachingPartState {
  content: string;
  expanded: boolean;
  uploadVoiceOver: boolean;
  voiceOverStartTime: Nullable<number>;
  voiceOverStopTime: Nullable<number>;
}

export type ITeachingPartProps = ITeachingPartOwnProps & ITeachingPartStateProps & ITeachingDispatchProps;

export class TeachingPartComponent extends React.Component<ITeachingPartProps, ITeachingPartState> {
  constructor(props: ITeachingPartProps) {
    super(props);
    const content = this.getTeachingContent(props);
    const voiceOver = this.getVoiceOver(props);

    this.state = {
      content,
      expanded: true,
      uploadVoiceOver: false,
      voiceOverStartTime: getVoiceOverStartAt(voiceOver),
      voiceOverStopTime: getVoiceOverStopAt(voiceOver),
    };
  }

  public componentDidMount() {
    const { resolve } = this.props;
    const voiceOver = this.getVoiceOver();
    const mediaId = R.pathOr(null, ["value", "mediaId"], voiceOver);

    if (!R.isNil(mediaId)) {
      resolve("mediaFiles", [`med.${mediaId}?display`]);
    }
  }

  public componentDidUpdate(oldProps: ITeachingPartProps) {
    if (changedPartOrLocale(this.props, oldProps)) {
      this.setState({ content: this.getTeachingContent() });
    }

    const oldVoiceOver = this.getVoiceOver(oldProps);
    const newVoiceOver = this.getVoiceOver();

    if (voiceOverMediaChanged(newVoiceOver, oldVoiceOver)) {
      this.props.resolve("mediaFiles", [`med.${newVoiceOver.value.mediaId}?display`]);
    }
  }

  public uploadVoiceOverAudio = (
    fileInputRef: React.RefObject<HTMLInputElement>,
    linkInputRef: React.RefObject<HTMLInputElement>
  ) => () => {
    const fileInput = fileInputRef.current;
    const linkInput = linkInputRef.current;

    if (R.isNil(fileInput) || R.isNil(linkInput)) {
      return;
    }

    const files = fileInput.files;
    const link = linkInput.value;

    const fileUploaded: boolean = !R.isNil(files) && !R.isNil(files.item(0));
    const linkUploaded: boolean = !R.isNil(link);

    if (fileUploaded) {
      return this.props
        .uploadFile((files as FileList).item(0) as File)
        .then(([error, response]) =>
          this.saveVoiceOverMediaAndClearInput(fileInput, error, response && response.data[0])
        );
    }

    if (linkUploaded) {
      return this.props
        .uploadFileViaUrl(link)
        .then(([error, response]) => this.saveVoiceOverMediaAndClearInput(linkInput, error, response && response.data));
    }

    return null;
  };

  public changeVOTime = (key: "voiceOverStartTime" | "voiceOverStopTime") => (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const value = event.target.value;
    const time = value === "" ? null : parseInt(value, 10);

    if (time !== null && !Number.isInteger(time)) {
      throw new Error(`Cannot parse ${key} time value`);
    }

    this.setState({ [key]: time } as any);
  };

  public submitVOTimes = (event: React.KeyboardEvent<HTMLInputElement>) => {
    const { voiceOverStartTime, voiceOverStopTime } = this.state;
    if (event.key === "Enter") {
      const startAt = voiceOverStartTime && voiceOverStartTime * 1000;
      const stopAt = voiceOverStopTime && voiceOverStopTime * 1000;
      this.props.teachingOperations.editVoiceOverTime(startAt, stopAt);
    }

    if (event.key === "Escape") {
      const voiceOver = this.getVoiceOver();
      this.setState({
        voiceOverStartTime: getVoiceOverStartAt(voiceOver),
        voiceOverStopTime: getVoiceOverStopAt(voiceOver),
      });
      event.currentTarget.blur();
    }
  };

  public render() {
    const { edit } = this.props;

    const localizedVoiceOver = this.getVoiceOver();

    const voiceOverFileEntry: Nullable<[string, ICourseResolveDisplayResponse]> =
      R.find(
        ([id, file]) => !R.isNil(localizedVoiceOver) && id.slice(4, 40) === localizedVoiceOver.value.mediaId,
        this.props.mediaFiles
      ) || null;

    const voiceOverFile: Nullable<ICourseResolveDisplayResponse> = voiceOverFileEntry && voiceOverFileEntry[1];
    const initialHTMLContent: string = this.getTeachingContent();

    return (
      <>
        {edit && (
          <div data-testid="editor-container">
            <HTMLEditor
              currentLocale={this.props.currentLocale}
              initialContent={initialHTMLContent}
              getContent={this.getTeachingContent}
              setContent={this.setTeachingContent}
              shouldAutoFocus={true}
              shouldAutoSave={this.editorShouldAutoSave}
              shouldResetState={this.editorShouldResetState}
              containerRef={this.props.containerRef}
            />
            <MarginSpace />
            <EditVoiceOverAudio
              changeStartTime={this.changeVOTime("voiceOverStartTime")}
              changeStopTime={this.changeVOTime("voiceOverStopTime")}
              enableVoiceOver={this.props.teachingOperations.enableVoiceOverAudio}
              localizedVoiceOver={localizedVoiceOver}
              submitVOTimes={this.submitVOTimes}
              uploadVoiceOverAudio={this.uploadVoiceOverAudio}
              voiceOverFile={voiceOverFile}
              voiceOverStartTime={this.state.voiceOverStartTime}
              voiceOverStopTime={this.state.voiceOverStopTime}
            />
          </div>
        )}
        <BackgroundPreviewBox backgroundImage={mkImageUrl(this.props.teachingPart.content.background)}>
          <ToggleDisplay shouldDisplay={!edit}>
            <HTMLRenderer source={this.state.content} />
          </ToggleDisplay>
        </BackgroundPreviewBox>
      </>
    );
  }

  public editorShouldResetState = (newProps: IHTMLEditorProps, oldProps: IHTMLEditorProps): boolean => {
    return newProps.currentLocale !== oldProps.currentLocale || newProps.initialContent !== oldProps.initialContent;
  };

  private editorShouldAutoSave = (newProps: IHTMLEditorProps, oldProps: IHTMLEditorProps): boolean => {
    const { edit } = this.props;
    return edit && newProps.currentLocale !== oldProps.currentLocale;
  };

  private setTeachingContent = (content: string, currentLocale?: PreferredLocale) => {
    if (content !== this.state.content) {
      this.props.teachingOperations.editHTMLContent(content, currentLocale);
    }
    this.props.toggleEdit();
  };

  private getTeachingContent = (props?: ITeachingPartOwnProps) =>
    courseInstructionPartViews.localizedContent(
      props ? props.teachingPart : this.props.teachingPart,
      props ? props.currentLocale : this.props.currentLocale
    ) || "";

  private getVoiceOver(props?: ITeachingPartProps): Nullable<ICourseVoiceOver> {
    return courseInstructionPartViews.localizedVoiceOver(
      props ? props.teachingPart : this.props.teachingPart,
      props ? props.currentLocale : this.props.currentLocale
    );
  }

  private saveVoiceOverMediaAndClearInput(
    input: HTMLInputElement,
    error: Nullable<string>,
    media: Nullable<IFileUploadResponseData>
  ) {
    if (!R.isNil(input)) {
      input.value = "";
    }
    if (error || !media) {
      throw Error(error || "No response");
    }
    const { mediaId } = media;
    this.props.teachingOperations.editVoiceOverMedia(mediaId.slice(4));
  }
}

export const TeachingPart = connect<ITeachingPartStateProps, ITeachingDispatchProps, ITeachingPartOwnProps, IState>(
  (state: IState, _: ITeachingPartOwnProps) => ({
    mediaFiles: state.App.CourseAuthor._.resolvedData.mediaFiles,
    uploadFile: state.App.CourseAuthor._.fileUploadApi.uploadFile,
    uploadFileViaUrl: state.App.CourseAuthor._.fileUploadApi.uploadFileViaUrl,
  }),
  (dispatch: Dispatch<IAction>, _: ITeachingPartOwnProps) => ({
    resolve: (key: string, requestData: string[]) => dispatch(Creators.resolveAuxiliaryData(key, requestData)),
    uploadVoiceOverAudio: (files: FileList | null, link: string | null) =>
      dispatch(Creators.uploadVoiceOverAudio(files, link)),
  })
)(TeachingPartComponent);

function voiceOverMediaChanged(
  newVoiceOver: Nullable<ICourseVoiceOver>,
  oldVoiceOver: Nullable<ICourseVoiceOver>
): newVoiceOver is ICourseVoiceOver {
  return (
    !R.isNil(newVoiceOver) &&
    R.pathOr(null, ["value", "media"], oldVoiceOver) !== R.pathOr(null, ["value", "media"], newVoiceOver)
  );
}

function changedPartOrLocale(props: ITeachingPartProps, oldProps: ITeachingPartProps): boolean {
  return props.teachingPart !== oldProps.teachingPart || props.currentLocale !== oldProps.currentLocale;
}

function getVoiceOverStopAt(voiceOver: Nullable<ICourseVoiceOver>): Nullable<number> {
  const stopAt = R.pathOr<Nullable<number>>(null, ["value", "stopAt"], voiceOver);
  return stopAt && stopAt / 1000;
}

function getVoiceOverStartAt(voiceOver: Nullable<ICourseVoiceOver>): Nullable<number> {
  const startAt = R.pathOr<Nullable<number>>(null, ["value", "startAt"], voiceOver);
  return startAt && startAt / 1000;
}
